import { helpers, type PluginConfiguration } from '../helpers'
import { icons } from '../icons'
import { installInlineTranslationPopup } from './translationEditPopup'

const TRANSLATION_DEBUG_STORAGE_KEY = 'fu-devtools-translations'

type CopySource = 'auto' | 'released' | 'unreleased'

type TranslationDebugSettings = {
  showKeys: boolean
  showFocalazyLinks: boolean
  checkVariables: boolean
  copySource: CopySource
  inlineEdit: boolean
  focalazyBaseUrl: string
}

const defaultSettings: TranslationDebugSettings = {
  showKeys: false,
  showFocalazyLinks: false,
  checkVariables: false,
  copySource: 'auto',
  inlineEdit: false,
  focalazyBaseUrl: 'https://translations.faceup.com',
}

const loadSettings = (): TranslationDebugSettings => {
  try {
    const stored = window.localStorage.getItem(TRANSLATION_DEBUG_STORAGE_KEY)
    if (stored) {
      return { ...defaultSettings, ...JSON.parse(stored) }
    }
  } catch {
    /* localStorage may not be available */
  }
  return defaultSettings
}

const currentSettings = loadSettings()

const createSelect = <T extends string>(
  id: string,
  label: string,
  description: string,
  options: readonly { value: T; label: string }[],
  currentValue: T
) =>
  helpers.createHTMLElements([
    {
      element: 'div',
      className: 'flex flex-col gap-1 text-white',
      content: () =>
        helpers.createHTMLElements([
          {
            element: 'label',
            className: 'text-sm font-medium',
            attributes: { for: `fu-tools-translation-select-${id}` },
            content: label,
          },
          {
            element: 'select',
            attributes: {
              id: `fu-tools-translation-select-${id}`,
              'data-fu-tools-translation-select': id,
            },
            className:
              'bg-[#334155] text-white rounded-md px-2 py-1 border-0 cursor-pointer hover:bg-[#1e293b] transition-colors',
            content: () =>
              helpers.createHTMLElements(
                options.map(opt => ({
                  element: 'option',
                  attributes: {
                    value: opt.value,
                    ...(opt.value === currentValue ? { selected: '' } : {}),
                  },
                  content: opt.label,
                }))
              ),
          },
          {
            element: 'div',
            className: 'text-sm text-[#6F91B7]',
            content: description,
          },
        ]),
    },
  ])

const createTextInput = (id: string, label: string, description: string, value: string) =>
  helpers.createHTMLElements([
    {
      element: 'div',
      className: 'flex flex-col gap-1 text-white',
      content: () =>
        helpers.createHTMLElements([
          {
            element: 'label',
            className: 'text-sm font-medium',
            attributes: { for: `fu-tools-translation-input-${id}` },
            content: label,
          },
          {
            element: 'input',
            attributes: {
              id: `fu-tools-translation-input-${id}`,
              'data-fu-tools-translation-input': id,
              type: 'text',
              value,
              spellcheck: 'false',
            },
            className:
              'bg-[#334155] text-white rounded-md px-2 py-1 border-0 hover:bg-[#1e293b] transition-colors',
          },
          {
            element: 'div',
            className: 'text-sm text-[#6F91B7]',
            content: description,
          },
        ]),
    },
  ])

const createToggle = (id: string, label: string, description: string, checked: boolean) =>
  helpers.createHTMLElements([
    {
      element: 'label',
      className: 'inline-flex gap-2 items-center cursor-pointer text-white',
      content: () =>
        helpers.createHTMLElements([
          {
            element: 'input',
            attributes: {
              type: 'checkbox',
              ...(checked ? { checked: '' } : {}),
              'data-fu-tools-translation-toggle': id,
            },
            className: 'sr-only peer',
          },
          {
            element: 'div',
            className:
              "relative w-7 h-4 bg-[#334155] rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-[#cbd5e1] after:border after:rounded-full after:w-3 after:h-3 after:transition-all peer-checked:bg-[#22d3ee]",
          },
          {
            element: 'span',
            content: label,
          },
        ]),
    },
    {
      element: 'div',
      className: 'text-sm text-[#6F91B7] ml-9',
      content: description,
    },
  ])

const config: PluginConfiguration = {
  name: 'Translations',
  id: 'translations',
  icon: icons.translations,
  extra: '',
  layout: {
    type: 'full',
    render: () => {
      const createdElements = helpers.createHTMLElements([
        {
          className:
            'pt-4 px-4 flex items-center justify-between mb-2 text-white gap-2 font-semibold',
          content: 'Translation Debug',
        },
        {
          className: 'px-4 pb-3 text-sm text-[#6F91B7] flex flex-col gap-1.5',
          content: () =>
            helpers.createHTMLElements([
              {
                element: 'div',
                content:
                  'Tools to help find and fix translations. Toggle the options below and click Apply — the page will reload with debug info visible.',
              },
              {
                element: 'div',
                className: 'mt-1 border-t border-[#334155] pt-2 flex flex-col gap-1',
                content: () =>
                  helpers.createHTMLElements([
                    {
                      element: 'div',
                      className: 'font-semibold text-[#8BA4C0]',
                      content: 'How to read the debug output:',
                    },
                    {
                      element: 'div',
                      content:
                        '\u{1F511} Raw keys — every translated text is replaced by its internal ID (e.g. "Administration.title"). Useful to find out which key a specific text belongs to.',
                    },
                    {
                      element: 'div',
                      content:
                        '\u{270F}\u{FE0F} Focalazy links — a small "Edit" button appears next to each visible translation so you can jump straight to Focalazy and fix it. For texts used as plain strings (e.g. tooltips, placeholders), a [URL] is appended instead because a clickable button cannot be placed inside a string.',
                    },
                    {
                      element: 'div',
                      content:
                        '\u{26A0}\u{FE0F} Variable check — a warning symbol (\u26A0) appears next to a translation when the translated text is missing a placeholder that the code expects (e.g. {count} or <bold>). For plain-string translations, [!] is appended instead. This usually means the translation needs to be updated in Focalazy.',
                    },
                  ]),
              },
            ]),
        },
        {
          className: 'flex items-center gap-2 mx-2',
          content: () =>
            helpers.createHTMLElements([
              {
                className:
                  'flex flex-1 justify-center border-0 text-white! transition-all bg-[#334155] hover:bg-[#1e293b]! rounded-md cursor-pointer p-2',
                content: '🚀 Apply',
                title: 'Save settings and reload the page',
                id: 'fu-tools-translations-apply',
              },
              {
                className:
                  'flex flex-1 justify-center border-0 text-white! transition-all bg-[#334155] hover:bg-[#1e293b]! rounded-md cursor-pointer p-2',
                content: '🧹 Reset',
                title: 'Clear all translation debug settings and reload',
                id: 'fu-tools-translations-reset',
              },
            ]),
        },
        {
          className: 'flex flex-col gap-4 p-4',
          content: () => {
            const fragment = document.createDocumentFragment()
            fragment.appendChild(
              createToggle(
                'showKeys',
                'Show raw translation keys',
                'Display message IDs instead of translated text, along with declared variables/wrappers.',
                currentSettings.showKeys
              )
            )
            fragment.appendChild(
              createToggle(
                'showFocalazyLinks',
                'Show Focalazy links',
                'Add clickable "Edit" links (FormattedMessage) or [URL] suffix (formatMessage) pointing to Focalazy.',
                currentSettings.showFocalazyLinks
              )
            )
            fragment.appendChild(
              createToggle(
                'checkVariables',
                'Check variables/wrappers',
                'Warn when a translation is missing declared {variable} or <wrapper> placeholders.',
                currentSettings.checkVariables
              )
            )
            fragment.appendChild(
              createSelect<CopySource>(
                'copySource',
                'Copy source',
                'Auto = unreleased outside production (faceup.com). Pick Released or Unreleased to force a specific snapshot regardless of environment.',
                [
                  { value: 'auto', label: 'Auto (default)' },
                  { value: 'unreleased', label: 'Unreleased (latest)' },
                  { value: 'released', label: 'Released (production snapshot)' },
                ],
                currentSettings.copySource
              )
            )
            fragment.appendChild(
              createToggle(
                'inlineEdit',
                'Inline edit popup',
                'Alt/Option-click any translation to edit the source value via Focalazy (draft only). Requires being logged in to Focalazy.',
                currentSettings.inlineEdit
              )
            )
            fragment.appendChild(
              createTextInput(
                'focalazyBaseUrl',
                'Focalazy base URL',
                'Origin of the Focalazy app the popup talks to. Defaults to https://translations.faceup.com',
                currentSettings.focalazyBaseUrl
              )
            )
            return fragment
          },
        },
      ])

      const tempDiv = document.createElement('div')
      tempDiv.className = 'relative'
      tempDiv.appendChild(createdElements)
      return tempDiv.outerHTML
    },
  },
  init: () => {
    // Inline edit popup is a runtime concern (event listener + DOM), not a
    // settings-baked-into-render one — install it as soon as the panel mounts
    // so the popup works even before Apply is pressed.
    installInlineTranslationPopup()

    const applyButton = document.querySelector('#fu-tools-translations-apply')
    applyButton?.addEventListener('click', () => {
      const toggles = document.querySelectorAll<HTMLInputElement>(
        '[data-fu-tools-translation-toggle]'
      )
      const selects = document.querySelectorAll<HTMLSelectElement>(
        '[data-fu-tools-translation-select]'
      )
      const inputs = document.querySelectorAll<HTMLInputElement>(
        '[data-fu-tools-translation-input]'
      )
      const settings: Record<string, boolean | string> = {}
      for (const toggle of toggles) {
        const key = toggle.getAttribute('data-fu-tools-translation-toggle')
        if (key) {
          settings[key] = toggle.checked
        }
      }
      for (const select of selects) {
        const key = select.getAttribute('data-fu-tools-translation-select')
        if (key) {
          settings[key] = select.value
        }
      }
      for (const input of inputs) {
        const key = input.getAttribute('data-fu-tools-translation-input')
        if (key) {
          settings[key] = input.value
        }
      }
      try {
        window.localStorage.setItem(TRANSLATION_DEBUG_STORAGE_KEY, JSON.stringify(settings))
      } catch {
        /* ignore */
      }
      window.location.reload()
    })

    const resetButton = document.querySelector('#fu-tools-translations-reset')
    resetButton?.addEventListener('click', () => {
      try {
        window.localStorage.removeItem(TRANSLATION_DEBUG_STORAGE_KEY)
      } catch {
        /* ignore */
      }
      window.location.reload()
    })
  },
  render: () => config.layout.render({}),
}

export const translations = config
